/// <amd-module name="Wit/Common/AutomationRulesService/AutomationRulesService" />
import { ITeamFieldData, ITeamSettings } from "Agile/Contracts/Common";
import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import {
    AutomationRuleNames,
    IAutomationRulesSettings,
    ShortStateInfo,
    WorkItemAutomationRuleSettings,
    WorkItemStateCategory
} from "Wit/Common/AutomationRulesService/Contracts/Contracts";
import * as Resources from "Wit/Common/AutomationRulesService/Resources";
import { WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature } from "Wit/Common/Constants/CustomerInteligence";
import { CoreField, WorkItemStateCategories } from "Wit/Common/Generated/Constants";
import { isClassificationPathEqualOrUnderRelative } from "Wit/Common/NodeHelpers";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { getWorkItemAbsoluteUrl } from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { CoreLinkTypes } from "Wit/WorkItemModel/OM";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";

export interface IAutomationRulesService extends IVssService {
    reloadRules(): Promise<void>;
    evaluateAutomationRules(workItem: IWorkItem): Promise<void>;
}

class AutomationRulesService extends VssService implements IAutomationRulesService {
    private _workItemModelService: IWorkItemModelService;
    private _workItemTypeMappedStates: Map<string, ShortStateInfo[]> = new Map();
    private _automationRules: Map<string, IAutomationRulesSettings[]> = new Map();
    private _teamSettingsService: ITeamSettingsService;
    private _currentTeamAreaNodes: ITeamFieldData[] = [];
    private _teamSettingsObservable: IReadonlyObservableValue<ITeamSettings | undefined>;
    private _parentIdsSet: Set<number> = new Set();
    private _telemetryService: IVssTelemetryService;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this._workItemModelService = this.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
        this._teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        this._teamSettingsObservable = this._teamSettingsService.getTeamSettingsObservable();
        this._telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");

        this._teamSettingsObservable.subscribe(x => {
            this.setCurrentTeamAreaNodes(x);
            this.loadAutomationRulesSettingsSync();
        });
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        this._teamSettingsObservable.unsubscribe(x => {
            this.setCurrentTeamAreaNodes(x);
            this.loadAutomationRulesSettingsSync();
        });
    }

    public async reloadRules(): Promise<void> {
        await this.loadAutomaitonRulesSettingsAsync();
    }

    public async evaluateAutomationRules(workItem: IWorkItem): Promise<void> {
        let areaPath = this._teamSettingsObservable.value?.teamFieldDefaultValue;

        const currentState = workItem.getFieldValueById(CoreField.State);
        const currentStateCategory = this.getStateCategory(currentState, workItem.workItemType.name);

        const isInSupportedCategories = this.checkIfWorkItemStateIsInSupportedCategory(workItem);

        if (!workItem.hasStateChanged() || !isInSupportedCategories) {
            return;
        }

        const belongsToCurrentTeam = this.currentTeamOwnsWorkItem(workItem.getFieldValueById(CoreField.AreaPath));
        const requestedAreaId = workItem.getFieldValueById(CoreField.AreaId);
        const requestedAreaPath = workItem.getFieldValueById(CoreField.AreaPath);
        if (!belongsToCurrentTeam && !this._automationRules.has(requestedAreaPath)) {
            await this.loadAutomaitonRulesSettingsAsync(requestedAreaId, requestedAreaPath);
        }

        if (!belongsToCurrentTeam) {
            areaPath = requestedAreaPath;
        }

        const parent = await this.getParent(workItem);

        //means we are changing the parent itself or we are already processing parent, no need to execute any rules.
        if (parent === undefined || this._parentIdsSet.has(parent.id)) {
            return;
        }

        const [isChangeParentWhenActiveEnabled, isChangeParentWhenCompletedEnabled, isChangeParentWhenResolvedEnabled] =
            this.getEnabledRulesByWorkItemTypeName(areaPath, parent.workItemType.name);

        if (isChangeParentWhenActiveEnabled && currentStateCategory === WorkItemStateCategory.InProgress) {
            this._parentIdsSet.add(parent.id);
            await this.processBottomUpHierarchy(workItem);
            this._parentIdsSet.delete(parent.id);
        }

        if (isChangeParentWhenCompletedEnabled && currentStateCategory === WorkItemStateCategory.Completed) {
            this._parentIdsSet.add(parent.id);
            await this.processTopDownHierarchy(parent, workItem, WorkItemStateCategory.Completed);
            this._parentIdsSet.delete(parent.id);
        }

        if (isChangeParentWhenResolvedEnabled && currentStateCategory === WorkItemStateCategory.Completed) {
            this._parentIdsSet.add(parent.id);
            await this.processTopDownHierarchy(parent, workItem, WorkItemStateCategory.Resolved);
            this._parentIdsSet.delete(parent.id);
        }
    }

    private async loadAutomaitonRulesSettingsAsync(areaId?: number, areaPath?: string): Promise<void> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const automationRulesData = await contributionService.getDataAsync<WorkItemAutomationRuleSettings>(
            "ms.vss-work-web.automation-rules-data-provider",
            { areaId },
            /* ignore cache */ true
        );
        this.setRulesData(automationRulesData, areaPath);
    }

    private loadAutomationRulesSettingsSync(): void {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const automationRulesData = contributionService.getData<WorkItemAutomationRuleSettings>("ms.vss-work-web.automation-rules-data-provider");
        this.setRulesData(automationRulesData);
    }

    private setRulesData(automationRulesData: WorkItemAutomationRuleSettings | undefined, targetAreaPath?: string): void {
        if (!automationRulesData) {
            return;
        }

        this._workItemTypeMappedStates = new Map(automationRulesData.workItemTypeMappedStates?.map(witms => [witms.workItemTypeName, witms.states]));

        const areaPath = targetAreaPath ? targetAreaPath : this._teamSettingsObservable.value?.teamFieldDefaultValue;
        const rules = automationRulesData.workItemAutomationRules;
        if (!rules || !areaPath) {
            return;
        }
        this._automationRules.set(areaPath, rules);
    }

    private getEnabledRulesByWorkItemTypeName(areaPath: string | undefined, workItemTypeName: string): [boolean, boolean, boolean] {
        if (!workItemTypeName || !areaPath) {
            return [false, false, false];
        }

        const rules = this._automationRules.get(areaPath)?.filter(x => x.workItemTypeName === workItemTypeName)[0];

        if (!rules) {
            return [false, false, false];
        }

        const isActiveEnabled = rules.rulesStates[AutomationRuleNames.ActivateParentWhenAnyChildActivated];
        const isCompletedEnabled = rules.rulesStates[AutomationRuleNames.CompleteParentWhenAllChildrenCompleted];
        const isResolvedEnabled = rules.rulesStates[AutomationRuleNames.ResolveParentWhenAllChildrenResolved];

        return [isActiveEnabled, isCompletedEnabled, isResolvedEnabled];
    }

    private isRuleEnabledByWorkItemTypeName(areaPath: string | undefined, workItemTypeName: string, ruleName: string): boolean {
        if (!workItemTypeName || !areaPath || !ruleName) {
            return false;
        }

        let rules = this._automationRules.get(areaPath)?.find(x => x.workItemTypeName === workItemTypeName);

        if (!rules) {
            //fallback to rules for the current team.
            let teamAreaPath = this._teamSettingsObservable.value?.teamFieldDefaultValue;
            if (teamAreaPath && this.currentTeamOwnsWorkItem(areaPath)) {
                rules = this._automationRules.get(teamAreaPath)?.find(x => x.workItemTypeName === workItemTypeName);
            }

            if (!rules) {
                return false;
            }
        }

        return rules.rulesStates[ruleName];
    }

    private async processBottomUpHierarchy(workItem: IWorkItem): Promise<void> {
        const currentChildState = workItem.getFieldValueById(CoreField.State);
        const isInProgressState = this.checkStateCategory(currentChildState, workItem.workItemType.name, WorkItemStateCategory.InProgress);

        if (!isInProgressState) {
            return;
        }

        let parentWorkItem = await this.getParent(workItem);
        const saveCandidates: IWorkItem[] = [];

        while (parentWorkItem !== undefined) {
            const parentAreaPath = parentWorkItem.getFieldValueById(CoreField.AreaPath);
            if (
                this.isRuleEnabledByWorkItemTypeName(
                    parentAreaPath,
                    parentWorkItem.workItemType.name,
                    AutomationRuleNames.ActivateParentWhenAnyChildActivated
                )
            ) {
                const shouldBeSaved = this.processActiveRule(parentWorkItem, workItem);
                if (shouldBeSaved) {
                    saveCandidates.push(parentWorkItem);
                }
            }

            parentWorkItem = await this.getParent(parentWorkItem);
        }

        if (saveCandidates.length > 0) {
            const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
            await savingService.saveWorkItemsBatchAsync(saveCandidates);
            this._telemetryService.publishEvent(WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature.TEAM_AUTOMATION_RULES, {
                workItemIds: saveCandidates.map(x => x.id),
                state: saveCandidates[0]?.getFieldValueById(CoreField.State),
                ruleName: AutomationRuleNames.ActivateParentWhenAnyChildActivated
            });
        }
    }

    private processActiveRule(workItem: IWorkItem, childWorkItem: IWorkItem): boolean {
        const currentState = workItem.getFieldValueById(CoreField.State);

        const inProgressState = this.getFirstStateFromCategory(workItem.workItemType.name, WorkItemStateCategory.InProgress);
        const isInProposedCategory = this.checkStateCategory(currentState, workItem.workItemType.name, WorkItemStateCategory.Proposed);

        // we change parent state ONLY when it has at least one valid state from InProgress category and currently is in Proposed category, otherwise we shouldn't change the state
        if (!inProgressState || !isInProposedCategory) {
            return false;
        }

        this.updateWorkItemState(workItem, inProgressState, childWorkItem, true);
        return true;
    }

    private async processTopDownHierarchy(parentWorkItem: IWorkItem, workItem: IWorkItem, targetCategory: WorkItemStateCategory): Promise<void> {
        const childrenLinks = parentWorkItem.getLinks()?.filter(link => link.linkData.LinkType === CoreLinkTypes.Child);

        if (childrenLinks?.length <= 0) {
            return;
        }

        const children = await this.createChildrenToParentMap(parentWorkItem);
        let isTheSameStateSetForAllChildren = true;

        const ruleName =
            targetCategory === WorkItemStateCategory.Completed
                ? AutomationRuleNames.CompleteParentWhenAllChildrenCompleted
                : AutomationRuleNames.ResolveParentWhenAllChildrenResolved;

        for (const item of children) {
            const currentChildState = item.getFieldValueById(CoreField.State);
            //since both rules Completed and Resolved rely on all children are in Completed state, we check it here below.
            const isInTargetStateCategory = this.checkStateCategory(currentChildState, item.workItemType.name, WorkItemStateCategory.Completed);

            if (!isInTargetStateCategory) {
                isTheSameStateSetForAllChildren = false;
                break;
            }
            isTheSameStateSetForAllChildren = true;
        }

        const parentAreaPath = parentWorkItem.getFieldValueById(CoreField.AreaPath);
        if (this.isRuleEnabledByWorkItemTypeName(parentAreaPath, parentWorkItem.workItemType.name, ruleName)) {
            const currentParentState = parentWorkItem.getFieldValueById(CoreField.State);
            const parentStateCategory = this.getStateCategory(currentParentState, parentWorkItem.workItemType.name);
            let parentState = this.getFirstStateFromCategory(parentWorkItem.workItemType.name, targetCategory);

            //There is one exception for Resolved rule: if there is no valid state in Resolved category, we look for any resolved state in the work item type.
            if (targetCategory === WorkItemStateCategory.Resolved && !parentState) {
                parentState = this.isStateExists(parentWorkItem.workItemType.name, WorkItemStateCategories.Resolved)
                    ? WorkItemStateCategories.Resolved
                    : undefined;
            }

            //we want to update the parent only if all conditions are met:
            //1. all children are in the same state category
            //2. parent is not already in the target state category
            //3. parent has a valid state from the target state category
            //4. parent's area path is owned by the current team
            if (
                isTheSameStateSetForAllChildren &&
                parentStateCategory &&
                parentStateCategory !== targetCategory &&
                parentState &&
                this.currentTeamOwnsWorkItem(parentAreaPath)
            ) {
                this.updateWorkItemState(parentWorkItem, parentState, workItem);
                this._telemetryService.publishEvent(WITCustomerIntelligenceArea, WITCustomerIntelligenceFeature.TEAM_AUTOMATION_RULES, {
                    workItemIds: [parentWorkItem.id],
                    state: parentWorkItem.getFieldValueById(CoreField.State),
                    ruleName: ruleName
                });
            }
        }
    }

    private async getParent(workItem: IWorkItem): Promise<IWorkItem | undefined> {
        const parentId = workItem.getFieldValueById(CoreField.Parent);

        if (parentId === undefined) {
            return undefined;
        }

        return await this._workItemModelService.getWorkItem(parentId);
    }

    private async createChildrenToParentMap(workItem: IWorkItem): Promise<IWorkItem[]> {
        const childrenLinks = workItem.getLinks()?.filter(link => link.linkData.LinkType === CoreLinkTypes.Child);

        if (childrenLinks?.length === 0) {
            return [];
        }

        const childrenIds = childrenLinks.reduce((acc, link) => {
            if (link.linkData.ID) {
                acc.push(link.linkData.ID);
            }
            return acc;
        }, [] as number[]);

        const children = await this._workItemModelService.getWorkItems(childrenIds);
        return children;
    }

    private updateWorkItemState(workItem: IWorkItem, state: string, childWorkItem: IWorkItem, ignoreSaving: boolean = false): void {
        const projectName = workItem.getFieldValueById(CoreField.TeamProject);
        const url = getWorkItemAbsoluteUrl(this.pageContext, childWorkItem.id, projectName);

        let commentText = this.createHtmlComment(childWorkItem, state, url);

        workItem.setFieldValueById(CoreField.History, commentText);
        workItem.setFieldValueById(CoreField.State, state);

        if (!ignoreSaving) {
            workItem.save();
        }
    }

    private createHtmlComment(workItem: IWorkItem, state: string, url: string): string {
        const anchorLink = `<a href=\"${url}\"> ${workItem.workItemType.name} ${workItem.id} </a>`;
        return `<div> ${format(Resources.AutomaitonRulesTriggeredCommentText, state, anchorLink)} </div>`;
    }

    private createMarkdownComment(workItem: IWorkItem, state: string, url: string): string {
        const link = `[${workItem.workItemType.name} ${workItem.id}](${url})`;
        return `${format(Resources.AutomaitonRulesTriggeredCommentText, state, link)}`;
    }

    private setCurrentTeamAreaNodes(teamSettings: ITeamSettings | undefined): void {
        if (!teamSettings) {
            return;
        }

        this._currentTeamAreaNodes = teamSettings.teamFieldValues;
    }

    private currentTeamOwnsWorkItem(areaPath: string): boolean {
        if (!areaPath) {
            return false;
        }

        return this._currentTeamAreaNodes.some(node =>
            isClassificationPathEqualOrUnderRelative(areaPath, node.value, node.includeChildren === true ? true : false)
        );
    }

    private getStateCategory(state: string, workItemTypeName: string): WorkItemStateCategory | undefined {
        const stateMap = this._workItemTypeMappedStates.get(workItemTypeName);

        if (!stateMap) {
            return undefined;
        }

        const stateCategory = stateMap.filter(x => x.state === state)[0]?.category;

        return stateCategory;
    }

    private checkIfWorkItemStateIsInSupportedCategory(workItem: IWorkItem): boolean {
        const state = workItem.getFieldValueById(CoreField.State);
        const workItemTypeName = workItem.workItemType.name;
        const stateCategory = this.getStateCategory(state, workItemTypeName);

        if (stateCategory === WorkItemStateCategory.InProgress || stateCategory === WorkItemStateCategory.Completed) {
            return true;
        }

        return false;
    }

    private checkStateCategory(state: string, workItemTypeName: string, category: WorkItemStateCategory): boolean {
        const stateMap = this._workItemTypeMappedStates.get(workItemTypeName);

        if (!stateMap) {
            return false;
        }

        const stateCategory = stateMap.filter(x => x.state === state)[0]?.category;

        if (stateCategory === category) {
            return true;
        }

        return false;
    }

    private getFirstStateFromCategory(workItemTypeName: string, category: WorkItemStateCategory): string | undefined {
        const stateMap = this._workItemTypeMappedStates.get(workItemTypeName);

        if (!stateMap) {
            return undefined;
        }

        const state = stateMap.filter(x => x.category === category)[0]?.state;

        return state;
    }

    private isStateExists(workItemTypeName: string, stateName: string): boolean {
        const stateMap = this._workItemTypeMappedStates.get(workItemTypeName);

        if (!stateMap) {
            return false;
        }

        const state = stateMap.filter(x => x.state === stateName)[0]?.state;

        return state !== undefined;
    }
}

const serviceName = "IAutomationRulesService";
Services.add(serviceName, { serviceFactory: AutomationRulesService });
